import React from 'react';
import PropTypes from 'prop-types';
import {Accordion} from '@/components/molecules';
import {buildImageHandlersFromProps} from '../shared/buildImageHandlersFromProps';
import GameDisplayAccordionItem from '../shared/GameDisplayAccordionItem';
import ImageUploadAccordionItem from '../shared/ImageUploadAccordionItem';
import BackgammonGuidelineModal from './BackgammonGuidelineModal';
import {
  BACKGAMMON_BACKGROUND_IMAGE_FIELDS,
  BACKGAMMON_BACKGROUND_SECTION_CONFIG
} from './backgammonImageFieldConfig';
import '../shared/ActivityDisplayAccordion.scss';

const BackgammonGameDisplayForm = ({
  languages,
  activeTabIndex,
  setActiveTabIndex,
  currentLangData,
  updateLangField,
  gameData,
  handleGameImageUpload,
  handleGameImageDelete,
  handleLogoImageUpload,
  handleLogoImageDelete,
  handleBackgroundMenuUpload,
  handleBackgroundMenuDelete,
  handleBackgroundImageUpload,
  handleBackgroundImageDelete,
  handleBackgroundVSUpload,
  handleBackgroundVSDelete,
  handleBackgroundWaitingUpload,
  handleBackgroundWaitingDelete,
  handleBackgroundExitUpload,
  handleBackgroundExitDelete,
  gameImageProgress,
  logoImageProgress,
  backgroundMenuProgress,
  backgroundImageProgress,
  backgroundVSProgress,
  backgroundWaitingProgress,
  backgroundExitProgress,
  gameImageValidationError = '',
  logoImageValidationError = '',
  backgroundMenuValidationError = '',
  backgroundImageValidationError = '',
  backgroundVSValidationError = '',
  backgroundWaitingValidationError = '',
  backgroundExitValidationError = '',
  handleGameImageFileError,
  handleLogoImageFileError,
  handleBackgroundMenuFileError,
  handleBackgroundImageFileError,
  handleBackgroundVSFileError,
  handleBackgroundWaitingFileError,
  handleBackgroundExitFileError,
  showGuidelines,
  setShowGuidelines
}) => {
  const backgroundImageHandlers = buildImageHandlersFromProps(
    {
      handleBackgroundMenuUpload,
      handleBackgroundMenuDelete,
      handleBackgroundMenuFileError,
      backgroundMenuProgress,
      backgroundMenuValidationError,
      handleBackgroundImageUpload,
      handleBackgroundImageDelete,
      handleBackgroundImageFileError,
      backgroundImageProgress,
      backgroundImageValidationError,
      handleBackgroundVSUpload,
      handleBackgroundVSDelete,
      handleBackgroundVSFileError,
      backgroundVSProgress,
      backgroundVSValidationError,
      handleBackgroundWaitingUpload,
      handleBackgroundWaitingDelete,
      handleBackgroundWaitingFileError,
      backgroundWaitingProgress,
      backgroundWaitingValidationError,
      handleBackgroundExitUpload,
      handleBackgroundExitDelete,
      handleBackgroundExitFileError,
      backgroundExitProgress,
      backgroundExitValidationError
    },
    BACKGAMMON_BACKGROUND_IMAGE_FIELDS
  );

  return (
    <div
      className='backgammon-display-form mb-8 flex flex-col gap-6'
      data-testid='backgammon-display-form'>
      <Accordion
        isDark
        size='large'
        content={
          <div className='w-full space-y-5'>
            <GameDisplayAccordionItem
              index={0}
              metadataProps={{
                languages,
                activeTabIndex,
                setActiveTabIndex,
                currentLangData,
                updateLangField,
                gameData,
                handleGameImageUpload,
                handleGameImageDelete,
                handleLogoImageUpload,
                handleLogoImageDelete,
                gameImageProgress,
                logoImageProgress,
                gameImageValidationError,
                logoImageValidationError,
                handleGameImageFileError,
                handleLogoImageFileError,
                setShowGuidelines
              }}
            />
            <ImageUploadAccordionItem
              index={1}
              label='Background Images'
              subTitle='Game background assets'
              sectionConfig={BACKGAMMON_BACKGROUND_SECTION_CONFIG}
              gameData={gameData}
              imageFields={BACKGAMMON_BACKGROUND_IMAGE_FIELDS}
              imageHandlers={backgroundImageHandlers}
              setShowGuidelines={setShowGuidelines}
            />
          </div>
        }
      />

      <BackgammonGuidelineModal
        showGuidelines={showGuidelines}
        setShowGuidelines={setShowGuidelines}
      />
    </div>
  );
};

BackgammonGameDisplayForm.propTypes = {
  languages: PropTypes.array.isRequired,
  activeTabIndex: PropTypes.number.isRequired,
  setActiveTabIndex: PropTypes.func.isRequired,
  currentLangData: PropTypes.object.isRequired,
  updateLangField: PropTypes.func.isRequired,
  gameData: PropTypes.object.isRequired,
  handleGameImageUpload: PropTypes.func.isRequired,
  handleGameImageDelete: PropTypes.func.isRequired,
  handleLogoImageUpload: PropTypes.func.isRequired,
  handleLogoImageDelete: PropTypes.func.isRequired,
  handleBackgroundMenuUpload: PropTypes.func.isRequired,
  handleBackgroundMenuDelete: PropTypes.func.isRequired,
  handleBackgroundImageUpload: PropTypes.func.isRequired,
  handleBackgroundImageDelete: PropTypes.func.isRequired,
  handleBackgroundVSUpload: PropTypes.func.isRequired,
  handleBackgroundVSDelete: PropTypes.func.isRequired,
  handleBackgroundWaitingUpload: PropTypes.func.isRequired,
  handleBackgroundWaitingDelete: PropTypes.func.isRequired,
  handleBackgroundExitUpload: PropTypes.func.isRequired,
  handleBackgroundExitDelete: PropTypes.func.isRequired,
  gameImageProgress: PropTypes.number,
  logoImageProgress: PropTypes.number,
  backgroundMenuProgress: PropTypes.number,
  backgroundImageProgress: PropTypes.number,
  backgroundVSProgress: PropTypes.number,
  backgroundWaitingProgress: PropTypes.number,
  backgroundExitProgress: PropTypes.number,
  gameImageValidationError: PropTypes.string,
  logoImageValidationError: PropTypes.string,
  backgroundMenuValidationError: PropTypes.string,
  backgroundImageValidationError: PropTypes.string,
  backgroundVSValidationError: PropTypes.string,
  backgroundWaitingValidationError: PropTypes.string,
  backgroundExitValidationError: PropTypes.string,
  handleGameImageFileError: PropTypes.func,
  handleLogoImageFileError: PropTypes.func,
  handleBackgroundMenuFileError: PropTypes.func,
  handleBackgroundImageFileError: PropTypes.func,
  handleBackgroundVSFileError: PropTypes.func,
  handleBackgroundWaitingFileError: PropTypes.func,
  handleBackgroundExitFileError: PropTypes.func,
  showGuidelines: PropTypes.bool.isRequired,
  setShowGuidelines: PropTypes.func.isRequired
};

export default React.memo(BackgammonGameDisplayForm);
